<script setup lang="ts">
import { AvatarWithFallback } from '@/components/ui/avatar'
import type { FeedVacancyItem } from '@/entities/feed';
import { toFeedCardModel } from '@/components/feed/feed-card.model';
import StoryPlayer from '@/components/video/StoryPlayer.vue';
import FeedSeenTag from '@/components/feed/FeedSeenTag.vue';
import FeedCardActions from '@/components/feed/FeedCardActions.vue';

const props = defineProps({
  canAct: { type: Boolean, default: true },
  responseContext: { type: Boolean, default: false },
  item: {
    type: Object as PropType<FeedVacancyItem>,
    required: true,
  },
  active: {
    type: Boolean,
    required: true,
  },
  preload: {
    type: Boolean,
    default: false,
  },
  favoriteActive: {
    type: Boolean,
    default: false,
  },
  /** Rendered in a sheet without the bottom navigation. */
  sheet: {
    type: Boolean,
    default: false,
  },
  /** The details cover the card: the video holds until they close. */
  suspended: {
    type: Boolean,
    default: false,
  },
  hasResponse: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  finished: [];
  action: ['primary' | 'favorite' | 'more'];
}>();

const card = computed(() => toFeedCardModel(props.item));
</script>

<template>
  <article class="vacancy-view relative h-full w-full overflow-hidden bg-black">
    <StoryPlayer
      :videos="item.videos"
      :active="active"
      :preload="preload"
      :sheet="sheet"
      :suspended="suspended"
      @finished="emit('finished')"
    >
      <template #default="player">
        <div
          class="vacancy-view__scrim"
          aria-hidden="true"
        />

        <button
          class="vacancy-view__sound focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          type="button"
          :aria-label="player.isMuted ? 'Включить звук' : 'Выключить звук'"
          @click.stop="player.toggleMuted"
        >
          <Icon
            :name="player.isMuted ? 'tabler:volume-off' : 'tabler:volume'"
            class="size-5"
            aria-hidden="true"
          />
        </button>

        <div class="vacancy-view__bottom" :class="sheet && '!pb-10'">
          <div class="vacancy-view__info">
            <div class="vacancy-view__identity">
              <AvatarWithFallback :src="card.avatar" :name="card.owner" class="size-8 shrink-0" />
              <p class="vacancy-view__eyebrow">
                {{ card.eyebrow }}
              </p>
            </div>
            <FeedSeenTag v-if="item.seen" />
            <h2 class="vacancy-view__title">
              {{ card.title }}
            </h2>
            <p v-if="card.supportingText" class="vacancy-view__salary">
              {{ card.supportingText }}
            </p>
          </div>
          <FeedCardActions
            :primary-label="canAct ? (props.responseContext ? 'Действия с откликом' : props.hasResponse ? 'Вы откликнулись' : 'Откликнуться') : undefined"
            :primary-disabled="props.hasResponse && !props.responseContext"
            :favorite-active="props.favoriteActive"
            @action="emit('action', $event)"
          />

          <div class="vacancy-view__chapter">
            <p class="m-0 truncate">
              {{ player.segment?.question?.trim() || 'О вакансии' }}
            </p>
            <time class="shrink-0 tabular-nums">{{ player.remainingLabel }}</time>
          </div>
        </div>
      </template>
    </StoryPlayer>
  </article>
</template>

<style scoped>
.vacancy-view__scrim {
  position: absolute;
  inset: 0;
  z-index: 20;
  pointer-events: none;
  background: linear-gradient(180deg, rgb(0 0 0 / 28%) 0%, transparent 18%, transparent 55%, rgb(0 0 0 / 60%) 100%);
}

.vacancy-view__sound {
  position: absolute;
  top: 4.75rem;
  right: 1rem;
  z-index: 30;
  display: grid;
  width: 2.5rem;
  height: 2.5rem;
  place-items: center;
  color: #fff;
  background: rgb(0 0 0 / 25%);
  border: 1px solid rgb(255 255 255 / 25%);
  border-radius: 999px;
  backdrop-filter: blur(4px);
}

.vacancy-view__info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  min-width: 0;
  color: #fff;
  pointer-events: none;
}

.vacancy-view__identity {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.vacancy-view__eyebrow,
.vacancy-view__title,
.vacancy-view__salary {
  max-width: 100%;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vacancy-view__eyebrow {
  min-width: 0;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.9;
}

.vacancy-view__title {
  max-width: 100%;
  font-size: 1.375rem;
  font-weight: 600;
  line-height: 1.25;
}

.vacancy-view__salary {
  font-size: 0.875rem;
  opacity: 0.9;
}

.vacancy-view__bottom {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0 1.5rem calc(6.75rem + env(safe-area-inset-bottom, 0px));
}

.vacancy-view__chapter {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
  color: #fff;
}

@media (max-width: 767px) {
  .vacancy-view__sound {
    display: none;
  }
}
</style>
